import React from 'react';
import ReactDOM from 'react-dom';

export interface ButtonControlledComponentProps {
  readonly buttonComponent: React.ComponentType<ButtonProps>;
  readonly childComponent: React.ComponentType<ButtonControlledChildComponentProps>;
  readonly disabled?: boolean; // defaults to false
  readonly testId: string;
  readonly stackingRootId?: string;
}

export interface ButtonProps {
  readonly isComponentShown: boolean;
  readonly disabled: boolean;
}

/** These props are passed to the child by the ButtonControlledComponent */
export interface ButtonControlledChildComponentProps {
  readonly anchorElement: HTMLElement;
  readonly testId: string;
  readonly closeMe: () => void;
}


export interface ButtonControlledComponentState {
  readonly isComponentShown: boolean;
}

/**
 * Generic wrapper for components whose display toggles on and off via a button, such as flyout or
 * dropdown menus, or other custom overlays. The button and the controlled component are both
 * specified via props.
 */
export class ButtonControlledComponent
    extends React.PureComponent<ButtonControlledComponentProps, ButtonControlledComponentState> {

  private anchorElement: HTMLElement;

  constructor(props: ButtonControlledComponentProps) {
    super(props);
    this.state = { isComponentShown: false };
  }

  public componentWillReceiveProps(nextProps: ButtonControlledComponentProps) {
    if (this.state.isComponentShown && nextProps.disabled) {
      this.closeChild();
    }
  }

  private setAnchorElementRef = (component: React.ReactInstance) => {
    this.anchorElement = ReactDOM.findDOMNode(component) as HTMLElement;
  }

  private toggleChildVisibility = (event: React.MouseEvent<HTMLDivElement>): void => this.state.isComponentShown ? this.closeChild() : this.openChild();

  private openChild = (): void => {
    if (!this.props.disabled) {
      this.setState({ isComponentShown: true });
    }
  }

  private closeChild = (): void => this.setState({ isComponentShown: false});

  public render() {
    const Child = this.props.childComponent;
    let childComponent: JSX.Element = null;
    if (this.state.isComponentShown) {
      childComponent = <Child anchorElement={ this.anchorElement }
                              closeMe={ this.closeChild }
                              testId={ this.props.testId } />;
    }

    const Button = this.props.buttonComponent;

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div>
        <div ref={this.setAnchorElementRef}
             onClick={this.toggleChildVisibility}
             data-tb-test-id={`${this.props.testId}-button`}>
          <Button isComponentShown={this.state.isComponentShown}
                  disabled={this.props.disabled} />
        </div>
        { childComponent }
      </div>
    );
  }
}
